import { Box, Paper, MenuItem as MuiMenuItem, MenuList as MuiMenuList, Radio as MuiRadio } from '@mui/material'
import { css } from '@emotion/react'
import styled from '@emotion/styled'

export const Container = styled(Box)`
  ${({ theme }) => css`
    min-height: 104px;
    width: 100%;
    padding: 16px;
    background: ${theme.colors.lightest};
    box-shadow: 8px 8px 8px 0 ${theme.colors.shadows.header};
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: space-between;
    gap: 16px;
    position: relative;
    z-index: 1000;

    @media (max-width: ${theme.screenSizes.laptop}) {
      padding-top: 28px;
      box-shadow: 0px 8px 8px 0 ${theme.colors.shadows.header};
    }
  `}
`

export const Content = styled(Box)`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
`

export const MobileMenuButton = styled(Box)`
  ${({ theme }) => css`
    display: none;
    width: 50px;
    height: 50px;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    margin-right: 16px;
    margin-left: -2px;
    margin-top: -2px;
    cursor: pointer;

    &:hover {
      transition: all ease 0.3s;
      background: ${theme.colors.background.hover};
    }

    @media (max-width: ${theme.screenSizes.laptop}) {
      display: flex;
    }
  `}
`

export const RightContent = styled(Box)`
  display: flex;
  align-items: center;
  gap: 16px;
`

export const UserBox = styled(Box)`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 16px;
    cursor: pointer;

    @media (max-width: ${theme.screenSizes.laptop}) {
      gap: 8px;
    }
  `}
`

export const UserImage = styled.img`
  ${({ theme }) => css`
    height: 48px;
    width: 48px;
    margin-right: 16px;
    border-radius: 50%;
    object-fit: cover;
    object-position: center;

    @media (max-width: ${theme.screenSizes.laptop}) {
      margin-right: 8px;
    }
  `}
`

export const UserName = styled(Box)`
  ${({ theme }) => css`
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 16px;
    color: ${theme.colors.secondary};

    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  `}
`

export const UserEmail = styled(UserName)`
  ${({ theme }) => css`
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 14px;
    color: ${theme.colors.gray.dark};
  `}
`

export const UserPosition = styled(UserEmail)`
  ${({ theme }) => css`
    color: ${theme.colors.primary};
  `}
`

export const LogoutButton = styled(UserPosition)`
  ${({ theme }) => css`
    color: ${theme.colors.primary};
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
  `}
`

export const Separator = styled(Box)`
  ${({ theme }) => css`
    width: 100%;
    height: 1px;
    background: ${theme.colors.gray[300]};
  `}
`

export const LastUpdate = styled(Box)`
  ${({ theme }) => css`
    text-align: right;
    font-size: 10px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: -0.2px;

    @media (max-width: ${theme.screenSizes.laptop}) {
      font-size: 9px;
    }
  `}
`

export const BellBox = styled(Box)`
  ${({ theme }) => css`
    height: 48px;
    display: flex;
    align-items: center;
    cursor: pointer;

    @media (max-width: ${theme.screenSizes.laptop}) {
      width: 32px;
      height: 32px;
    }
  `}
`

export const InputsBox = styled(Box)`
  ${({ theme }) => css`
    display: flex;
    gap: 16px;

    @media (max-width: ${theme.screenSizes.laptop}) {
      display: none;
    }
  `}
`

export const SelectBox = styled(Box)`
  ${({ theme }) => css`
    width: 200px;

    @media (max-width: ${theme.screenSizes.laptop}) {
      display: none;
    }
  `}
`

export const AutocompleteBox = styled(Box)`
  ${({ theme }) => css`
    width: 300px;

    @media (max-width: ${theme.screenSizes.laptop}) {
      display: none;
    }
  `}
`

export const MenuPaper = styled(Paper)`
  border-radius: 8px !important;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1) !important;
  padding: 0 !important;
`

export const MenuList = styled(MuiMenuList)`
  ${({ theme }) => css`
    padding: 16px !important;
    overflow-y: auto;
    overflow-x: hidden;
    display: flex;
    flex-direction: column;
    gap: 12px;

    &::-webkit-scrollbar {
      width: 8px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
      border-radius: 8px;
    }
    &::-webkit-scrollbar-thumb {
      background-color: ${theme.colors.background.hover};
      border-radius: 8px;
      transition: background-color 0.3s ease;
    }
    &::-webkit-scrollbar-thumb:hover {
      background-color: ${theme.colors.background.hover};
    }

    scrollbar-width: thin;
    scrollbar-color: ${theme.colors.background.hover} transparent;
  `}
`

export const MenuItem = styled(MuiMenuItem)`
  ${({ theme }) => css`
    font-size: 14px;
    color: ${theme.colors.secondary};
    height: 48px;
    padding: 12px 16px;

    display: flex;
    align-items: center;
    gap: 8px;

    &:hover,
    &:focus,
    &.Mui-selected {
      background-color: ${theme.colors.lightest} !important;
    }
  `}
`

export const Radio = styled(MuiRadio)`
  ${({ theme }) => css`
    padding: 0;
    margin: 0;
    align-items: center;
    justify-content: center;

    .MuiSvgIcon-root {
      font-size: 24px;
    }

    color: ${theme.colors.secondary};

    &.Mui-checked {
      color: ${theme.colors.secondary};
    }
  `}
`

export const RadioText = styled.span`
  width: 100%;
  display: block;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;

  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
`

export const SeeMore = styled(Box)`
  ${({ theme }) => css`
    display: none;
    width: 100%;

    @media (max-width: ${theme.screenSizes.laptop}) {
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      font-size: 12px;
      font-weight: 500;
      line-height: 12px;
      gap: 16px;
      color: ${theme.colors.secondary};

      svg {
        margin-top: 2px;
      }
    }
  `}
`

export const SeeMoreButton = styled(Box)`
  ${() => css`
    width: fit-content;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    align-self: flex-end;
    gap: 8px;
    margin-right: 16px;
    cursor: pointer;
  `}
`

export const CloseInputsBox = styled(Box)`
  ${() => css`
    display: flex;
    flex-direction: column;
    gap: 16px;
  `}
`

export const UserMenuBox = styled(Box)`
  ${({ theme }) => css`
    height: 68px;
    display: flex;
    align-items: flex-end;
    gap: 16px;

    @media (max-width: ${theme.screenSizes.laptop}) {
      height: fit-content;
      align-items: center;
    }
  `}
`
